import { useTheme } from "@/context/ThemeContext";
import yLogoLight from "@/assets/brand/y-logo.png";
import yLogoDark from "@/assets/brand/y-logo-dark.png";
import { yLogoInline, yLogoDarkInline } from "@/assets/brand/yLogoInline";
import biMarkLight from "@/assets/brand/bi-mark.png";
import biMarkDark from "@/assets/brand/bi-mark-dark.png";
import biWordmarkLight from "@/assets/brand/bi-wordmark.png";
import biWordmarkDark from "@/assets/brand/bi-wordmark-dark.png";
import welcomeLight from "@/assets/brand/welcome.png";
import welcomeDark from "@/assets/brand/welcome-dark.png";

/**
 * Web port of iOS `src/components/Brand.tsx`.
 *
 * Serves two things:
 *   - `useBrandSources()` — returns the correct light/dark PNG for each brand
 *     asset based on the resolved theme.
 *   - `YMark` / `BrontoIntelligence` — the two lockups used across the app,
 *     rendered from real PNGs (not text/gradient hacks) so the marks look
 *     identical to the iOS version.
 *
 * Aspect ratios come from the trimmed source dimensions (see iOS Brand.tsx).
 */

// Trimmed source dimensions from iOS Brand.tsx
const Y_ASPECT = 758 / 882;
const BI_MARK_ASPECT = 470 / 413;
const BI_WORDMARK_ASPECT = 934 / 132;

export function useBrandSources() {
  const { theme } = useTheme();
  const dark = theme === "dark";
  return {
    yLogo: dark ? yLogoDark : yLogoLight,
    biMark: dark ? biMarkDark : biMarkLight,
    biWordmark: dark ? biWordmarkDark : biWordmarkLight,
    welcome: dark ? welcomeDark : welcomeLight,
  };
}

/**
 * The signature cursive rainbow "Y" — the in-app logo mark.
 * `height` in px, width computed from the trimmed aspect ratio so no crop.
 *
 * Sources: we use the base64-inlined data-URI variants (yLogoInline*) so
 * the mark is present in memory the instant this component mounts.  The
 * previous file-import approach fired a separate HTTP request after the
 * JS bundle had loaded, producing a visible flash on the login/signup
 * screens on cold loads.  With inline URIs, YMark renders on first
 * paint with no network dependency.
 */
export function YMark({ height = 30, className = "", testId }) {
  const { theme } = useTheme();
  const src = theme === "dark" ? yLogoDarkInline : yLogoInline;
  return (
    <img
      src={src}
      alt="BrontoY"
      aria-label="BrontoY"
      draggable={false}
      className={`block select-none ${className}`}
      style={{ height, width: height * Y_ASPECT }}
      data-testid={testId}
    />
  );
}

/**
 * HeaderBrand — the wordmark shown in the Home top bar.  On iOS this is the
 * Y mark followed by "BrontoY" set in Grand Hotel cursive.  We reproduce
 * that exactly: image Y at the given height + "BrontoY" script text at the
 * same optical size, sitting on the same baseline.
 */
export function HeaderBrand({ height = 27, className = "" }) {
  const fontSize = height * 1.05;
  return (
    <span
      className={`inline-flex items-center gap-2 ${className}`}
      aria-label="BrontoY"
    >
      <YMark height={height} />
      <span
        className="font-grand-hotel text-foreground leading-none"
        style={{ fontSize, marginTop: -Math.round(height * 0.12) }}
      >
        BrontoY
      </span>
    </span>
  );
}

/**
 * "By Bronto Intelligence" lockup — BI mark centered above the wordmark.
 * When the wordmark PNGs land we swap the text fallback for the image.
 */
export function BrontoIntelligence({
  markHeight = 34,
  wordmarkHeight = 16,
  className = "",
}) {
  const { biMark, biWordmark } = useBrandSources();
  return (
    <div
      className={`flex flex-col items-center ${className}`}
      aria-label="Bronto Intelligence"
    >
      <img
        src={biMark}
        alt="Bronto Intelligence"
        draggable={false}
        className="block select-none"
        style={{ height: markHeight, width: markHeight * BI_MARK_ASPECT }}
      />
      <img
        src={biWordmark}
        alt=""
        draggable={false}
        className="block select-none"
        style={{
          height: wordmarkHeight,
          width: wordmarkHeight * BI_WORDMARK_ASPECT,
          marginTop: 8,
        }}
      />
    </div>
  );
}

export { BI_MARK_ASPECT, BI_WORDMARK_ASPECT, Y_ASPECT };
